import React from "react";
import { RSD } from "../../lib/api";
import { Banknote, CreditCard, Receipt } from "lucide-react";
import { PonesiCoinIcon } from "../PonesiCoinIcon";

export const showCash = (pm) => pm !== "CARD";
export const showCard = (pm) => pm !== "CASH";

function MoneyCard({ label, amount, orders, tone, icon: Icon, testid, big }) {
  return (
    <div data-testid={testid} className={`rounded-2xl border-2 p-3 ${tone.wrap}`}>
      <div className="flex items-center gap-1.5">
        <Icon size={14} className={tone.icon} />
        <p className={`text-[10px] font-black uppercase tracking-[0.12em] ${tone.icon}`}>{label}</p>
      </div>
      <p data-testid={`${testid}-amount`} className={`font-display font-black ${big ? "text-3xl" : "text-2xl"} mt-1 leading-none ${tone.text}`}>
        {RSD(amount)}
      </p>
      <p className="text-xs text-slate-500 mt-1.5">{orders} {orders === 1 ? "porudžbina" : "porudžbina"}</p>
    </div>
  );
}

const TONES = {
  cash: { wrap: "bg-white border-brand-green/30", icon: "text-brand-green", text: "text-brand-green" },
  card: { wrap: "bg-white border-blue-300", icon: "text-blue-600", text: "text-blue-700" },
  coins: { wrap: "bg-white border-amber-300", icon: "text-amber-600", text: "text-amber-700" },
  total: { wrap: "bg-slate-900 border-slate-900", icon: "text-white/70", text: "text-white" },
};

// GOTOVINA / KARTICA / UKUPNO — never merged, always server values.
export function ShiftMoneyCards({ shift, paymentMethod, big = false }) {
  const cash = showCash(paymentMethod);
  const card = showCard(paymentMethod);
  const cols = 1 + (cash ? 1 : 0) + (card ? 1 : 0);
  return (
    <div data-testid="shift-money-cards" className={`grid gap-2 ${cols === 3 ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-1 sm:grid-cols-2"}`}>
      {cash && <MoneyCard label="Gotovina" amount={shift.cash_sales} orders={shift.cash_orders}
        tone={TONES.cash} icon={Banknote} testid="shift-cash-card" big={big} />}
      {card && <MoneyCard label="Kartica" amount={shift.card_sales} orders={shift.card_orders}
        tone={TONES.card} icon={CreditCard} testid="shift-card-card" big={big} />}
      <MoneyCard label="Ukupno" amount={shift.total_sales} orders={shift.orders}
        tone={TONES.total} icon={Receipt} testid="shift-total-card" big={big} />
    </div>
  );
}

/** PONESI COINS is a separate tender: never cash, never card, never in the expected drawer. */
export function ShiftCoinsCard({ shift }) {
  const orders = Number(shift.coin_orders || 0);
  if (!orders) return null;
  return (
    <div data-testid="shift-coins-card" className="rounded-2xl border-2 bg-white border-amber-300 p-3">
      <div className="flex items-center gap-1.5">
        <PonesiCoinIcon size={14} />
        <p className="text-[10px] font-black uppercase tracking-[0.12em] text-amber-700">Ponesi Coins</p>
      </div>
      <p data-testid="shift-coins-redeemed" className="font-display font-black text-2xl mt-1 leading-none text-amber-700 tabular-nums">
        {new Intl.NumberFormat("sr-RS").format(Number(shift.coins_redeemed || 0))}
      </p>
      <p className="text-xs text-slate-500 mt-1.5">
        {orders} porudžbina · referentno {RSD(shift.coin_reference_value_rsd)}
      </p>
      <p className="text-[10px] text-slate-400 mt-1">Ne ulazi u kasu, gotovinu ni karticu.</p>
    </div>
  );
}

// Secondary: where the money came from.
export function ShiftSourceBreakdown({ shift }) {
  const s = shift.by_source || { PONESI: { revenue: 0, orders: 0 }, VISTA: { revenue: 0, orders: 0 } };
  return (
    <div data-testid="shift-source-breakdown" className="grid grid-cols-2 gap-2 text-sm">
      <div className="bg-white rounded-xl border p-2.5">
        <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Ponesi.rs</p>
        <p data-testid="shift-src-ponesi" className="font-bold">{RSD(s.PONESI.revenue)}</p>
        <p className="text-xs text-slate-400">{s.PONESI.orders} porudžbina</p>
      </div>
      <div className="bg-white rounded-xl border p-2.5">
        <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Vista</p>
        <p data-testid="shift-src-vista" className="font-bold">{RSD(s.VISTA.revenue)}</p>
        <p className="text-xs text-slate-400">{s.VISTA.orders} porudžbina</p>
      </div>
    </div>
  );
}

// Cash drawer only — card payments are never part of this reconciliation.
export function CashReconciliation({ expected, counted, difference }) {
  const diff = Number(difference || 0);
  const tone = counted === null || counted === undefined ? "text-slate-400"
    : diff === 0 ? "text-brand-green" : diff < 0 ? "text-brand-red" : "text-brand-orange";
  const Row = ({ label, value, testid, cls = "" }) => (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-[11px] font-black uppercase tracking-wider text-slate-400">{label}</span>
      <span data-testid={testid} className={`font-display font-black ${cls}`}>{value}</span>
    </div>
  );
  return (
    <div data-testid="shift-cash-reconciliation" className="bg-white rounded-2xl border p-3 divide-y">
      <Row label="Očekivano u kasi" value={RSD(expected)} testid="shift-expected-drawer" />
      <Row label="Prebrojano" value={counted === null || counted === undefined ? "—" : RSD(counted)} testid="shift-counted-cash" />
      <Row label="Razlika" testid="shift-cash-difference" cls={tone}
        value={counted === null || counted === undefined ? "—" : `${diff > 0 ? "+" : ""}${RSD(diff)}`} />
    </div>
  );
}
